import {
	CdkFixedSizeVirtualScroll,
	CdkVirtualForOf,
	CdkVirtualScrollViewport,
} from '@angular/cdk/scrolling';
import { AsyncPipe } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	forwardRef,
	input,
	signal,
} from '@angular/core';
import {
	ControlValueAccessor,
	FormsModule,
	NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import { TuiAutoFocus } from '@taiga-ui/cdk';
import { TuiCheckbox, TuiDataList, TuiInput } from '@taiga-ui/core';
import { TuiSearch } from '@taiga-ui/layout';
import { BehaviorSubject, map } from 'rxjs';

export type SelectSearchOption<T extends string> = {
	/**
	 * The label is the text that will display if the option is selected in the UI
	 */
	label?: string;

	/**
	 * The text to display as the button option while searching the dropdown
	 */
	text: string;

	/**
	 * The value of the option to propagate and save for filtering
	 */
	value: T;
};

@Component({
	selector: 'mar-multiselect-search',
	standalone: true,
	imports: [
		TuiSearch,
		FormsModule,
		TuiInput,
		AsyncPipe,
		TuiDataList,
		CdkVirtualScrollViewport,
		CdkFixedSizeVirtualScroll,
		CdkVirtualForOf,
		TuiCheckbox,
		TranslocoPipe,
		TuiAutoFocus,
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
	templateUrl: 'multiselect-search.component.html',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => MultiselectSearchComponent),
			multi: true,
		},
	],
	host: {
		class: 'min-w-72 flex flex-col bg-white min-h-72',
	},
})
export class MultiselectSearchComponent implements ControlValueAccessor {
	disabled = signal<boolean>(false);
	value = new Set<string>();

	readonly search$ = new BehaviorSubject<string>('');

	readonly options = input<string[]>([]);
	readonly filteredOptions$ = this.search$.pipe(
		map((value) => {
			const search = value.trim().toLowerCase();
			return this.options().filter((option) => {
				const text = option.trim().toLowerCase();
				if (text.includes(search)) return true;
				return false;
			});
		}),
	);

	readonly allLabel = input<string>('generic.select-all');

	allChecked(options: string[]) {
		return options.every((option) => this.value.has(option));
	}

	onSelect(option: string) {
		if (this.value.has(option)) {
			this.value.delete(option);
		} else {
			this.value.add(option);
		}
		this.invokeValue();
	}

	async onSelectAll(options: string[]) {
		if (this.allChecked(options)) {
			options.forEach((option) => this.value.delete(option));
		} else {
			options.forEach((option) => this.value.add(option));
		}
		this.invokeValue();
	}

	invokeValue() {
		this.onChange(Array.from(this.value).sort((a, b) => (a > b ? 1 : -1)));
	}

	writeValue(value: string[]) {
		this.value = new Set(value);
	}

	setDisabledState(isDisabled: boolean) {
		this.disabled.set(isDisabled);
	}

	// eslint-disable-next-line @typescript-eslint/no-empty-function
	onChange: any = (_value: string[]) => {}; // Callback for value changes

	// eslint-disable-next-line @typescript-eslint/no-empty-function
	onTouched: any = () => {}; // Callback for touch events

	registerOnChange(fn: any): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: any): void {
		this.onTouched = fn;
	}
}
